import { css } from 'styled-system/css';
import { Container } from '../../components/landing/Container';
import { Reveal } from '../../components/landing/Reveal';
import { FounderPortrait } from './FounderPortrait';
import { FounderSignoff } from './FounderSignoff';
import { letterText } from './letterText';

const lede =
  'I built Exis because I love spreadsheets, and I think they deserve an upgrade.';

const paragraphs = [
  'Over my career as a software engineer I became known for reactive programming. I’ve traveled around the world giving conference talks on how data propagates through a program. Those same principles have always powered spreadsheets: change one cell, and everything that depends on it updates. A spreadsheet is a reactive programming environment that millions of people already know how to use.',
  'But spreadsheets weren’t made for the age of AI. AI agents need full access to read and write your sheets to be truly useful, and yet you don’t want them running loose, editing the cells that matter most to your finances, your project plans, or your business.',
  'That’s why I built Exis with risk control built in. Agents can do real work in your spreadsheets, and you stay in charge of what actually lands. You can ramp up automation with AI without losing control.',
  'I wholeheartedly recommend giving Exis a try. If you’re reading this, you probably have at least one spreadsheet you really care about. Import it into Exis, see how it feels, and tell me what you’d like to see in the product.',
  'I’m here for you, and I react positively to feedback.',
];

export function FounderLetter() {
  return (
    <section
      aria-label="A note from the founder"
      className={css({
        paddingTop: { base: '32', md: '40' },
        paddingBottom: { base: '16', md: '24' },
      })}
    >
      <Container>
        <article
          className={css({
            display: 'grid',
            gridTemplateColumns: {
              base: 'minmax(0, 1fr)',
              lg: 'minmax(0, 5fr) minmax(0, 7fr)',
            },
            columnGap: '16',
            rowGap: { base: '10', md: '12' },
            maxWidth: { base: '[640px]', lg: 'none' },
            marginInline: 'auto',
          })}
        >
          <Reveal
            className={css({
              lg: { position: 'sticky', top: '32', alignSelf: 'start' },
            })}
          >
            <header
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: { base: '8', lg: '12' },
              })}
            >
              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4',
                })}
              >
                <span
                  className={css({ textStyle: 'eyebrow', color: 'text.muted' })}
                >
                  A note from the founder
                </span>
                <h1
                  className={css({
                    textStyle: 'display',
                    fontSize: { base: '4xl', lg: '5xl' },
                    color: 'text.primary',
                    margin: '0',
                  })}
                >
                  About Exis
                </h1>
              </div>
              <FounderPortrait />
            </header>
          </Reveal>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '6',
              paddingTop: { lg: '8' },
            })}
          >
            <Reveal
              delay={80}
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: '6',
              })}
            >
              <p
                className={css({
                  fontSize: { base: 'lg', md: 'xl' },
                  lineHeight: 'normal',
                  fontWeight: 'medium',
                  color: 'text.primary',
                  textWrap: 'pretty',
                  margin: '0',
                })}
              >
                {lede}
              </p>
              {paragraphs.map((text) => (
                <p key={text} className={css(letterText)}>
                  {text}
                </p>
              ))}
            </Reveal>
            <Reveal>
              <FounderSignoff />
            </Reveal>
          </div>
        </article>
      </Container>
    </section>
  );
}
